import React, { PropsWithChildren } from 'react';
import { Link, ToOptions } from '@tanstack/react-router';

import { Separator } from '@/components/ui/separator';
import { SidebarTrigger } from '@/components/ui/sidebar';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { cn } from '@/lib/utils';

interface IProps extends PropsWithChildren {
  title?: string;
  subtitle?: string;
  breadcrumbItems?: (ToOptions & { label: string })[];
  withSeparator?: boolean;
}

const PageContainer: React.FC<IProps> = ({
  title,
  subtitle,
  breadcrumbItems,
  withSeparator,
  children,
}) => (
  <>
    <header className="bg-[#f9f9f9] z-50 sticky top-0 flex h-16 shrink-0 items-center gap-2 border-b px-4">
      <SidebarTrigger className="-ml-1" />
      <Separator orientation="vertical" className="mr-2 h-4" />
      <Breadcrumb>
        <BreadcrumbList>
          {breadcrumbItems?.map(({ label, ...props }) => (
            <>
              <BreadcrumbItem className="hidden md:block">
                <BreadcrumbLink asChild>
                  <Link {...props}>{label}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="hidden md:block" />
            </>
          ))}

          <BreadcrumbItem>
            <BreadcrumbPage>{title}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    </header>
    <div
      className={cn(
        'flex min-w-0 flex-1 flex-col gap-4 p-4 pt-4',
        'w-full max-w-screen-2xl mx-auto',
      )}
    >
      {(title || subtitle) && (
        <div className={cn('space-y-1', { 'mb-6': !withSeparator })}>
          <h2 className="text-2xl font-bold tracking-tight">{title}</h2>
          {subtitle && <p className="text-muted-foreground">{subtitle}</p>}
        </div>
      )}
      {withSeparator && <Separator className="my-6" />}
      {children}
    </div>
  </>
);

export default PageContainer;
